/** Independent editorial interactions, with editor lifecycle cleanup. */
class RsEditorialMedia extends HTMLElement {
  connectedCallback() {
    if (this._abort) return;
    this._abort = new AbortController();
    const signal = this._abort.signal;
    this._motion = matchMedia('(prefers-reduced-motion: reduce)');
    this._videos = Array.from(this.querySelectorAll('video')).map(video => {
      const toggle = video.parentElement.querySelector('[data-rs-video-toggle]');
      const state = { video, toggle, desired: !this._motion.matches, visible: false };
      if (toggle) {
        video.controls = false;
        toggle.hidden = false;
        toggle.addEventListener('click', () => {
          state.desired = video.paused;
          if (state.desired) this._play(state); else video.pause();
        }, { signal });
        const update = () => {
          const paused = video.paused;
          toggle.setAttribute('aria-label', paused ? toggle.dataset.playLabel : toggle.dataset.pauseLabel);
          const icon = toggle.querySelector('[data-video-icon]');
          if (icon) icon.textContent = paused ? '▶' : 'Ⅱ';
        };
        video.addEventListener('play', update, { signal });
        video.addEventListener('pause', update, { signal });
        update();
      }
      return state;
    });
    this._observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (entry.target === this) this.classList.toggle('is-in-view', entry.isIntersecting);
        const state = this._videos.find(item => item.video === entry.target);
        if (!state) continue;
        state.visible = entry.isIntersecting;
        if (state.visible && state.desired && !document.hidden) this._play(state);
        else state.video.pause();
      }
    }, { threshold: .2 });
    this._observer.observe(this);
    this._videos.forEach(state => this._observer.observe(state.video));
    document.addEventListener('visibilitychange', () => {
      this._videos.forEach(state => {
        if (!document.hidden && state.visible && state.desired) this._play(state);
        else state.video.pause();
      });
    }, { signal });
    this._motion.addEventListener('change', () => {
      if (this._motion.matches) this._videos.forEach(state => { state.desired = false; state.video.pause(); });
    }, { signal });
    this._timers = Array.from(this.querySelectorAll('[data-rs-countdown]')).map(element => {
      const raw = element.dataset.rsCountdown;
      const valid = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?(?:Z|[+-]\d{2}:\d{2})$/.test(raw || '');
      return { element, end: valid ? Date.parse(raw) : NaN };
    });
    if (this._timers.length) {
      this._updateTimers();
      this._interval = setInterval(() => this._updateTimers(), 1000);
    }
  }
  _play(state) {
    const result = state.video.play();
    if (result && typeof result.catch === 'function') result.catch(() => {});
  }
  _updateTimers() {
    for (const { element, end } of this._timers) {
      const remaining = Math.ceil((end - Date.now()) / 1000);
      if (!Number.isFinite(remaining) || remaining <= 0) { element.hidden = true; continue; }
      const parts = { days: Math.floor(remaining / 86400), hours: Math.floor(remaining / 3600) % 24, minutes: Math.floor(remaining / 60) % 60, seconds: remaining % 60 };
      for (const [key, value] of Object.entries(parts)) {
        const target = element.querySelector(`[data-${key}]`);
        if (target) target.textContent = String(value).padStart(2, '0');
      }
      element.hidden = false;
    }
  }
  disconnectedCallback() {
    this._abort?.abort(); this._abort = null;
    this._observer?.disconnect();
    clearInterval(this._interval);
    this._videos?.forEach(state => state.video.pause());
  }
}
class RsEditorialMarquee extends HTMLElement {
  connectedCallback() {
    if (this._abort) return;
    this._abort = new AbortController();
    const signal = this._abort.signal;
    this._toggle = this.querySelector('[data-marquee-toggle]');
    if (!this._toggle) return;
    this._toggle.hidden = false;
    this._motion = matchMedia('(prefers-reduced-motion: reduce)');
    this._paused = this._motion.matches;
    this._visible = true;
    this.setAttribute('data-running', '');
    this._toggle.addEventListener('click', () => { this._paused = !this._paused; this._update(); }, { signal });
    this._motion.addEventListener('change', () => { if (this._motion.matches) this._paused = true; this._update(); }, { signal });
    this._observer = new IntersectionObserver(entries => {
      this._visible = entries[0].isIntersecting;
      this._update();
    });
    this._observer.observe(this);
    document.addEventListener('visibilitychange', () => this._update(), { signal });
    document.addEventListener('shopify:block:select', event => { if (this.contains(event.target)) { this._paused = true; this._update(); } }, { signal });
    this._update();
  }
  _update() {
    const paused = this._paused || !this._visible || document.hidden;
    this._toggle.hidden = this._motion.matches;
    this.toggleAttribute('data-paused', paused);
    this._toggle.setAttribute('aria-label', this._paused ? this._toggle.dataset.playLabel : this._toggle.dataset.pauseLabel);
    const icon = this._toggle.querySelector('[data-marquee-icon]');
    if (icon) icon.textContent = this._paused ? '▶' : 'Ⅱ';
  }
  disconnectedCallback() { this._abort?.abort(); this._abort = null; this._observer?.disconnect(); }
}
if (!customElements.get('rs-editorial-media')) customElements.define('rs-editorial-media', RsEditorialMedia);
if (!customElements.get('rs-editorial-marquee')) customElements.define('rs-editorial-marquee', RsEditorialMarquee);
